import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Mail, Phone, Paperclip, Mic, Smile, Send, CheckSquare, Loader2, File as FileIcon, Download, X, Square, Trash2, Clock, Sparkles, FileCheck2, Pencil, Check, User, Calendar, CalendarClock, List, AlertTriangle, ArrowRightLeft, ExternalLink } from 'lucide-react';
import { Negociacao, NegociacaoNota, NegociacaoLog, Tarefa } from '../../../../types';
import { supabase } from '../../../../src/lib/supabase';
import { logUsageEvent } from '../../../../src/lib/logger';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { useToast } from '../../../../src/contexts/ToastContext';
import CriarVisitaModal from '../../../../src/components/CriarVisitaModal';
import GestaoDetailsModal from '../../../Gestao/GestaoDetailsModal';
import ConfirmModal from '../../../../src/components/ConfirmModal';
import { ComentarioTexto, corDoUsuario } from '../../../../src/components/comentarios/ComentarioTexto';
import VisitaModal, { VisitaParadaInfo, VisitaConclusao } from '../../Rotas/VisitaModal';
import { concluirVisitaTarefa, concluirTarefa, reagendarTarefa, cancelarTarefa } from '../../../../src/lib/visitaTarefa';
import GerenciarTarefaModal, { GerenciarTarefaResultado } from './GerenciarTarefaModal';
import { normalizarModalidade } from '../../../../src/lib/modalidade';
import { approveDiscount, rejectDiscount } from '../../../../src/lib/discountApproval';
import ConversasWhatsApp from './ConversasWhatsApp';

interface DetailFeedProps {
    negotiation: Negociacao;
    onUpdate?: () => void;
    onClose?: () => void;
}

type TimelineFilter = 'tudo' | 'notas' | 'tarefas' | 'anexos' | 'mensagens';

interface TimelineEvent {
    id: string;
    type: 'note' | 'task' | 'log' | 'message' | 'attachment';
    date: Date;
    user: { name: string; avatar?: string };
    content: string;
    subContent?: React.ReactNode;
    data: any; // Original object
}

const DetailFeed: React.FC<DetailFeedProps> = ({ negotiation, onUpdate, onClose }) => {
    const { user, profile } = useAuth();
    const { addToast } = useToast();
    const [note, setNote] = useState('');
    const [activeTab, setActiveTab] = useState<'note' | 'email' | 'whatsapp'>('note');
    const [filter, setFilter] = useState<TimelineFilter>('tudo');
    const [saving, setSaving] = useState(false);

    // Data States
    const [events, setEvents] = useState<TimelineEvent[]>([]);
    const [taskCount, setTaskCount] = useState(0);
    const [isVisitaModalOpen, setIsVisitaModalOpen] = useState(false);
    const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
    // Tarefa do tipo visita → painel "Concluir Visita" inline (sem ir para a aba Tarefas)
    const [visitaTarefa, setVisitaTarefa] = useState<any | null>(null);
    // Tarefa comum → modal "Gerenciar Tarefa" inline (sem navegar para a aba Tarefas)
    const [gerenciarTarefa, setGerenciarTarefa] = useState<any | null>(null);

    // Input States
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);

    // Edit States
    const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
    const [editingContent, setEditingContent] = useState('');
    const [editSaving, setEditSaving] = useState(false);
    const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);
    const [showDeleteApproveConfirm, setShowDeleteApproveConfirm] = useState(false);
    const [pendingDeleteNoteId, setPendingDeleteNoteId] = useState<string | null>(null);

    // Voice Recording States
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [showVoiceModal, setShowVoiceModal] = useState(false);
    const [tempAudioFile, setTempAudioFile] = useState<File | null>(null);

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    // Settings
    const [webhookUrl, setWebhookUrl] = useState<string | null>(null);

    useEffect(() => {
        fetchData();
        fetchSettings();

        // Realtime Subscription for automatic transcription updates
        const channel = supabase
            .channel(`negociacao_updates_${negotiation.id}`)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'negociacao_notas',
                    filter: `negociacao_id=eq.${negotiation.id}`
                },
                () => {
                    fetchData(); // Refresh feed on insertion/update (e.g. transcription arrived)
                }
            )
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'negociacao_logs',
                    filter: `negociacao_id=eq.${negotiation.id}`
                },
                () => {
                    fetchData(); // Refresh feed on new logs
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [negotiation]);

    // Timer Logic
    useEffect(() => {
        if (isRecording) {
            timerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
        } else {
            if (timerRef.current) clearInterval(timerRef.current);
        }
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [isRecording]);

    const fetchSettings = async () => {
        const { data } = await supabase
            .from('system_settings')
            .select('value')
            .eq('key', 'voice_transcription_webhook_url')
            .single();
        if (data) setWebhookUrl(data.value);
    };

    const fetchData = async () => {
        try {
            // 1. Fetch Notes (includes Messages & Attachments)
            const { data: notesData } = await supabase
                .from('negociacao_notas')
                .select('*')
                .eq('negociacao_id', negotiation.id)
                .order('created_at', { ascending: false });

            // 2. Fetch Tasks
            const { data: tasksData } = await supabase
                .from('tarefas')
                .select('*')
                .eq('negociacao_id', negotiation.id)
                .order('created_at', { ascending: false });

            // 3. Fetch Logs
            const { data: logsData } = await supabase
                .from('negociacao_logs')
                .select('*')
                .eq('negociacao_id', negotiation.id)
                .order('created_at', { ascending: false });

            // 4. Fetch Task Comments
            const taskIds = tasksData?.map(t => t.id) || [];
            let commentsMap: Record<string, any[]> = {};
            if (taskIds.length > 0) {
                const { data: commentsData } = await supabase
                    .from('tarefa_comentarios')
                    .select('*')
                    .in('tarefa_id', taskIds)
                    .order('created_at', { ascending: true });

                commentsData?.forEach(c => {
                    if (!commentsMap[c.tarefa_id]) commentsMap[c.tarefa_id] = [];
                    commentsMap[c.tarefa_id].push(c);
                });
            }

            // Count open tasks
            const openTasks = tasksData?.filter((t: any) => ['a_iniciar', 'em_andamento', 'aguardando'].includes(t.status)).length || 0;
            setTaskCount(openTasks);

            // Fetch Profiles manually
            const userIds = new Set<string>();
            notesData?.forEach((n: any) => n.user_id && userIds.add(n.user_id));
            tasksData?.forEach((t: any) => {
                if (t.responsavel_id) userIds.add(t.responsavel_id);
                if (t.solicitante_id) userIds.add(t.solicitante_id);
            });
            logsData?.forEach((l: any) => l.user_id && userIds.add(l.user_id));

            // Add comment authors
            Object.values(commentsMap).flat().forEach((c: any) => c.user_id && userIds.add(c.user_id));

            // Add creator ID if available
            if (negotiation.user_id) userIds.add(negotiation.user_id);

            let profilesMap: Record<string, any> = {};
            if (userIds.size > 0) {
                const { data: profiles } = await supabase
                    .from('profiles')
                    .select('*')
                    .in('id', Array.from(userIds));
                profiles?.forEach(p => profilesMap[p.id] = p);
            }

            // Process Timeline Events
            let combined: TimelineEvent[] = [];

            // Process Notes
            notesData?.forEach((n: any) => {
                const profile = profilesMap[n.user_id];
                const isAttachment = !!n.arquivo_url;
                const isMessage = n.tipo === 'email' || n.tipo === 'whatsapp';

                combined.push({
                    id: `note-${n.id}`,
                    type: isAttachment ? 'attachment' : (isMessage ? 'message' : 'note'),
                    date: new Date(n.created_at),
                    user: { name: profile?.full_name || 'Usuário', avatar: profile?.avatar_url },
                    content: n.conteudo || (isAttachment ? 'Enviou um anexo' : 'Nota'),
                    data: n
                });
            });

            // Process Tasks
            tasksData?.forEach((t: any) => {
                const solicitor = t.solicitante_id ? profilesMap[t.solicitante_id] : null;
                const solicitorName = solicitor?.full_name || 'Sistema';
                const responsible = t.responsavel_id ? profilesMap[t.responsavel_id] : null;
                const taskComments = commentsMap[t.id] || [];

                combined.push({
                    id: `task-${t.id}`,
                    type: 'task',
                    date: new Date(t.created_at),
                    user: { name: solicitorName, avatar: solicitor?.avatar_url },
                    content: `Criou a tarefa: ${t.titulo}`,
                    subContent: <span className={`text-xs px-2 py-0.5 rounded ${t.status === 'concluido' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'}`}>{t.status.replace('_', ' ')}</span>,
                    data: {
                        ...t,
                        solicitante_profile: solicitor,
                        responsavel_profile: responsible,
                        comentarios: taskComments.map(c => ({
                            ...c,
                            user: profilesMap[c.user_id]
                        }))
                    }
                });
            });

            // Process Logs
            logsData?.forEach((l: any) => {
                const profile = profilesMap[l.user_id];
                combined.push({
                    id: `log-${l.id}`,
                    type: 'log',
                    date: new Date(l.created_at),
                    user: { name: profile?.full_name || 'Sistema' },
                    content: l.detalhes ? `${l.acao}\n${l.detalhes}` : l.acao,
                    data: l
                });
            });

            // Mock Logs if empty (Creation Log)
            // Even if logs exist, we might want to show the creation log if it's not in the DB?
            // Usually creation log is not in `negociacao_logs` unless we insert it at creation.
            // Let's assume we always want to show it if we are showing the history start.
            // But strict request was "if empty". Let's stick to "if empty" or check if a "created" log exists.
            // For now, I'll keep the logic "if !logsData || logsData.length < 5" maybe?
            // The user complained it shows "Sistema".
            // I will add it always if no "criou o negócio" log is found.

            const hasCreationLog = combined.some(e => e.content.toLowerCase().includes('criou o negócio'));

            if (!hasCreationLog) {
                const creatorProfile = negotiation.user_id ? profilesMap[negotiation.user_id] : null;
                const creatorName = creatorProfile?.full_name || 'Sistema';

                combined.push({
                    id: 'mock-creation',
                    type: 'log',
                    date: new Date(negotiation.created_at),
                    user: { name: creatorName },
                    content: 'criou o negócio',
                    data: {}
                });
            }

            // Sort DESC
            combined.sort((a, b) => b.date.getTime() - a.date.getTime());
            setEvents(combined);
        } catch (error) {
            console.error("Error fetching timeline:", error);
        }
    };

    // --- Input Handlers ---

    const handleEmojiClick = (emoji: string) => {
        setNote(prev => prev + emoji);
        setShowEmojiPicker(false);
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
        }
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const startRecording = async () => {
        if (navigator.mediaDevices) {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                streamRef.current = stream;

                mediaRecorderRef.current = new MediaRecorder(stream);
                audioChunksRef.current = [];

                mediaRecorderRef.current.ondataavailable = (event) => {
                    if (event.data.size > 0) {
                        audioChunksRef.current.push(event.data);
                    }
                };

                mediaRecorderRef.current.onstop = () => {
                    try {
                        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                        const audioName = `nota_de_voz_${Date.now()}.webm`;
                        const audioFile = new File([audioBlob], audioName, { type: 'audio/webm' });

                        setTempAudioFile(audioFile);
                        setShowVoiceModal(true);
                        setIsRecording(false);

                        if (streamRef.current) {
                            streamRef.current.getTracks().forEach(track => track.stop());
                            streamRef.current = null;
                        }
                    } catch (e) {
                        console.error("Error inside onstop:", e);
                        alert("Erro ao processar áudio.");
                        setIsRecording(false);
                    }
                };

                mediaRecorderRef.current.start();
                setRecordingTime(0);
                setIsRecording(true);
            } catch (err) {
                console.error("Error accessing microphone:", err);
                alert("Erro ao acessar microfone. Verifique as permissões.");
            }
        } else {
            alert("Navegador não suporta gravação de áudio.");
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current) {
            if (mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop();
            }
        }
    };

    const discardRecording = () => {
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.onstop = null;
            if (mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop();
            }
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }

        setIsRecording(false);
        setRecordingTime(0);
        audioChunksRef.current = [];
    };

    const confirmVoiceNote = () => {
        if (tempAudioFile) {
            setSelectedFile(tempAudioFile);
        }
        setShowVoiceModal(false);
        setTempAudioFile(null);
        setRecordingTime(0);
    };

    const handleSave = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (saving) return;
        if (!note.trim() && !selectedFile) return;

        setSaving(true);

        try {
            let fileUrl = null;
            let fileType = null;
            let fileName = null;

            if (selectedFile) {
                const fileExt = selectedFile.name.split('.').pop();
                const path = `notes/${negotiation.id}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

                const { error: uploadError } = await supabase.storage
                    .from('tarefas-anexos')
                    .upload(path, selectedFile);

                if (uploadError) throw uploadError;

                const { data: { publicUrl } } = supabase.storage
                    .from('tarefas-anexos')
                    .getPublicUrl(path);

                fileUrl = publicUrl;
                fileType = selectedFile.type;
                fileName = selectedFile.name;
            }

            const { data: insertedNote, error: dbError } = await supabase.from('negociacao_notas').insert({
                negociacao_id: negotiation.id,
                user_id: user?.id,
                conteudo: note,
                tipo: 'nota',
                arquivo_url: fileUrl,
                arquivo_tipo: fileType,
                arquivo_nome: fileName
            }).select().single();

            if (dbError) throw dbError;
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'adicionar_nota_negociacao', origem: 'acao' });

            // Trigger Transcription Webhook if it's an audio file
            if (fileType?.startsWith('audio/')) {
                if (webhookUrl && fileUrl) {
                    try {
                        // Fire and forget, don't await response
                        fetch(webhookUrl, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                type: 'voice_transcription_request',
                                note_id: insertedNote.id,
                                audio_url: fileUrl,
                                negotiation_id: negotiation.id,
                                user_id: user?.id,
                                created_at: new Date().toISOString()
                            })
                        }).catch(err => console.error("Webhook trigger failed", err));
                    } catch (e) {
                        console.error("Error triggering webhook", e);
                    }
                }

                // Polling for transcription (Fallback to Realtime)
                // User requested ~5s delay for processing
                setTimeout(() => fetchData(), 5000);
                setTimeout(() => fetchData(), 10000);
                setTimeout(() => fetchData(), 20000);
            }

            // Reset
            setNote('');
            setSelectedFile(null);
            await fetchData();
            addToast('Nota salva com sucesso!', 'success');

        } catch (error: any) {
            console.error('Error saving note:', error);
            // Case-specific feedback for AbortError
            if (error.name === 'AbortError') {
                addToast('A requisição foi cancelada pelo navegador. Tente novamente.', 'warning');
            } else {
                addToast('Erro ao salvar: ' + (error.message || 'Erro desconhecido'), 'error');
            }
        } finally {
            setSaving(false);
        }
    };

    // --- Edit / Delete Handlers ---

    const handleStartEdit = (noteId: string, content: string) => {
        setEditingNoteId(noteId);
        setEditingContent(content);
    };

    const handleCancelEdit = () => {
        setEditingNoteId(null);
        setEditingContent('');
    };

    const handleUpdateNote = async (noteId: string) => {
        if (!editingContent.trim()) return;
        setEditSaving(true);
        try {
            const { error } = await supabase
                .from('negociacao_notas')
                .update({ conteudo: editingContent })
                .eq('id', noteId);
            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'editar_nota_negociacao', origem: 'acao' });
            setEditingNoteId(null);
            setEditingContent('');
            fetchData();
        } catch (error: any) {
            console.error('Error updating note:', error);
            addToast('Erro ao atualizar nota: ' + error.message, 'error');
        } finally {
            setEditSaving(false);
        }
    };

    const handleDeleteNote = async (noteId: string) => {
        try {
            const { error } = await supabase
                .from('negociacao_notas')
                .delete()
                .eq('id', noteId);
            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'excluir_nota_negociacao', origem: 'acao' });
            setDeletingNoteId(null);
            fetchData();
        } catch (error: any) {
            console.error('Error deleting note:', error);
            addToast('Erro ao excluir nota: ' + error.message, 'error');
        }
    };

    const handleApproveDiscount = async (_noteId: string) => {
        setSaving(true);
        try {
            if (!user) return;
            await approveDiscount(negotiation, user.id, profile?.full_name);
            fetchData();
            if (onUpdate) onUpdate();
        } catch (e: any) {
            console.error("Error approving discount:", e);
            addToast("Erro ao aprovar desconto: " + (e.message || "Erro desconhecido"), 'error');
        } finally { setSaving(false); }
    };

    const handleRejectDiscount = async (_noteId: string) => {
        setSaving(true);
        try {
            if (!user) return;
            await rejectDiscount(negotiation, user.id, profile?.full_name);
            fetchData();
            if (onUpdate) onUpdate();
        } catch (e: any) {
            console.error("Error rejecting discount:", e);
            addToast("Erro ao rejeitar desconto: " + (e.message || "Erro desconhecido"), 'error');
        } finally { setSaving(false); }
    };

    const handleApproveDelete = async (noteId: string) => {
        setPendingDeleteNoteId(noteId);
        setShowDeleteApproveConfirm(true);
    };

    const executeApproveDelete = async () => {
        setShowDeleteApproveConfirm(false);
        setSaving(true);
        try {
            // Note: UCs are auto-deleted via ON DELETE CASCADE on negociacao_id FK

            // Delete task comments first (FK to tarefas)
            const { data: relatedTasks } = await supabase
                .from('tarefas')
                .select('id')
                .eq('negociacao_id', negotiation.id);

            if (relatedTasks && relatedTasks.length > 0) {
                const taskIds = relatedTasks.map(t => t.id);
                await supabase
                    .from('comentarios_tarefas')
                    .delete()
                    .in('tarefa_id', taskIds);
            }

            // Delete Tasks
            await supabase
                .from('tarefas')
                .delete()
                .eq('negociacao_id', negotiation.id);

            // Delete negotiation notes (FK to negociacoes)
            await supabase
                .from('negociacao_notas')
                .delete()
                .eq('negociacao_id', negotiation.id);

            // Delete negotiation logs (FK to negociacoes)
            await supabase
                .from('negociacao_logs')
                .delete()
                .eq('negociacao_id', negotiation.id);

            // Delete analise cadastral (FK to negociacoes)
            await supabase
                .from('analise_cadastral')
                .delete()
                .eq('negociacao_id', negotiation.id);

            // Delete audit records (FK to negociacoes)
            await supabase
                .from('negociacoes_documentos_audit')
                .delete()
                .eq('negociacao_id', negotiation.id);

            // Delete comissions (FK to negociacoes)
            await supabase
                .from('comissao_parceiros')
                .delete()
                .eq('negociacao_id', negotiation.id);

            await supabase
                .from('comissao_vendedor_negociacoes')
                .delete()
                .eq('negociacao_id', negotiation.id);

            // Delete pre-venda records (FK to negociacoes)
            await supabase
                .from('pre_venda')
                .delete()
                .eq('negociacao_id', negotiation.id);

            // Delete whatsapp conversations (FK to negociacoes)
            await supabase
                .from('whatsapp_conversas')
                .delete()
                .eq('negociacao_id', negotiation.id);

            // Delete Negotiation
            const { error } = await supabase
                .from('negociacoes')
                .delete()
                .eq('id', negotiation.id);

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'excluir_negociacao', origem: 'acao' });

            // Refresh board and close modal (SPA-safe, avoids 404 on Vercel)
            if (onUpdate) onUpdate();
            if (onClose) onClose();
        } catch (error: any) {
            console.error('Error approving delete:', error);
            addToast('Erro ao excluir negociação: ' + error.message, 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleRejectDelete = async (noteId: string) => {
        setSaving(true);
        try {
            // Note: We don't DELETE the request anymore because we want to maintain history
            // and often RLS prevents managers from deleting notes they didn't create.
            // Presence of [DELETE_REJECTED] in the feed will be used to hide the buttons.
            const { error: insertError } = await supabase
                .from('negociacao_notas')
                .insert({
                    negociacao_id: negotiation.id,
                    user_id: user?.id,
                    conteudo: `[DELETE_REJECTED] Solicitação de exclusão recusada pelo gerente.`,
                    tipo: 'nota'
                });

            if (insertError) throw insertError;
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'rejeitar_exclusao_negociacao', origem: 'acao' });

            const notifyId = negotiation.user_id || negotiation.responsavel_id;
            if (notifyId) {
                await supabase.from('notifications').insert({
                    user_id: notifyId,
                    type: 'negotiation_assigned',
                    title: 'Pedido de Exclusão Recusado',
                    content: `Seu pedido de exclusão para a negociação "${negotiation.cliente_nome}" foi recusado pelo gerente.`,
                    link: `/crm?negociacaoId=${negotiation.id}`,
                    read: false
                });
            }
            fetchData();
            if (onUpdate) onUpdate();
            window.dispatchEvent(new CustomEvent('refreshKanban'));
        } catch (e: any) {
            console.error('Error rejecting delete:', e);
            addToast('Erro ao recusar exclusão: ' + (e.message || 'Erro desconhecido'), 'error');
        } finally { setSaving(false); }
    };

    // --- Render Helpers ---

    // Find the LATEST approval request note ID to only show buttons on that one
    const latestApprovalRequestId = events
        .filter(e => e.content && e.content.startsWith('[APPROVAL_REQUEST]'))
        .sort((a, b) => b.date.getTime() - a.date.getTime())[0]?.id;

    const filteredEvents = events.filter(e => {
        if (filter === 'tudo') return true;
        if (filter === 'notas') return e.type === 'note';
        if (filter === 'tarefas') return e.type === 'task';
        if (filter === 'anexos') return e.type === 'attachment' || (e.type === 'task' && e.data.anexos_url?.length > 0);
        if (filter === 'mensagens') return e.type === 'message';
        return true;
    });

    const emojis = ['👍', '👎', '😄', '🎉', '🚀', '🔥', '✅', '❤️', '👀', '🤔'];

    return (
        <>
            <div className="space-y-6">

                {/* Input Area */}
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm overflow-visible relative z-20">
                    <div className="px-4 pt-3 border-b border-gray-100 dark:border-slate-800/50 flex gap-4">
                        {['note', 'email', 'whatsapp'].map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab as any)}
                                className={`pb-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 capitalize
                                ${activeTab === tab ? 'border-primary text-gray-800 dark:text-slate-100' : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 dark:text-slate-200'}
                            `}
                            >
                                {tab === 'note' && <MessageSquare size={16} />}
                                {tab === 'email' && <Mail size={16} />}
                                {tab === 'whatsapp' && <Phone size={16} />}
                                {tab}
                            </button>
                        ))}
                    </div>

                    <div className="p-4">
                        {activeTab === 'note' ? (
                            <>
                                <textarea
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder="Adicione uma nota, anexo ou mensagem..."
                                    className="w-full min-h-[80px] text-sm text-gray-700 dark:text-slate-200 placeholder-gray-400 dark:placeholder-slate-500 outline-none resize-none bg-transparent"
                                />

                                {/* Attachments Preview */}
                                {selectedFile && (
                                    <div className="flex items-center gap-2 mt-2 p-2 bg-gray-50 dark:bg-slate-800 rounded-lg text-xs text-gray-600 dark:text-slate-300 w-fit">
                                        <FileIcon size={14} />
                                        <span className="truncate max-w-[200px]">{selectedFile.name}</span>
                                        <button onClick={() => setSelectedFile(null)} className="text-gray-400 dark:text-slate-500 hover:text-red-500 dark:text-red-400">
                                            <X size={14} />
                                        </button>
                                    </div>
                                )}

                                <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100 dark:border-slate-800/50">
                                    {/* Hidden Input */}
                                    <input type="file" ref={fileInputRef} hidden onChange={handleFileSelect} />

                                    {/* Emojis Popover */}
                                    {showEmojiPicker && (
                                        <div className="absolute bottom-16 left-4 bg-white dark:bg-slate-900 shadow-xl border border-gray-100 dark:border-slate-700/50 rounded-xl p-2 grid grid-cols-5 gap-1 z-30">
                                            {emojis.map(emoji => (
                                                <button key={emoji} onClick={() => handleEmojiClick(emoji)} className="p-2 hover:bg-gray-50 dark:hover:bg-slate-800/80 dark:bg-slate-800 rounded text-xl">
                                                    {emoji}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {isRecording ? (
                                        <div className="flex items-center gap-3 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-1 shadow-sm">
                                            <button
                                                onClick={discardRecording}
                                                className="text-gray-400 dark:text-slate-500 hover:text-red-500 dark:text-red-400 transition-colors p-1"
                                                title="Descartar"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                            <span className="text-sm font-variant-numeric text-blue-500 w-[40px] text-center">
                                                {formatTime(recordingTime)}
                                            </span>
                                            <button
                                                onClick={stopRecording}
                                                className="text-red-500 dark:text-red-400 hover:text-red-600 transition-colors p-1"
                                                title="Parar e Salvar"
                                            >
                                                <div className="w-4 h-4 bg-red-400 rounded-sm hover:bg-red-500 transition-colors" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={startRecording}
                                                className="p-2 text-gray-400 dark:text-slate-500 hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800/80 hover:text-gray-600 dark:hover:text-slate-300 dark:text-slate-300 rounded-lg transition-colors"
                                                title="Gravar áudio"
                                            >
                                                <Mic size={18} />
                                            </button>
                                            <button
                                                onClick={() => fileInputRef.current?.click()}
                                                className="p-2 text-gray-400 dark:text-slate-500 hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800/80 hover:text-gray-600 dark:hover:text-slate-300 dark:text-slate-300 rounded-lg transition-colors"
                                                title="Anexar arquivo"
                                            >
                                                <Paperclip size={18} />
                                            </button>
                                            <button
                                                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                                className="p-2 text-gray-400 dark:text-slate-500 hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800/80 hover:text-gray-600 dark:hover:text-slate-300 dark:text-slate-300 rounded-lg transition-colors"
                                                title="Adicionar emoji"
                                            >
                                                <Smile size={18} />
                                            </button>
                                        </div>
                                    )}

                                    <button
                                        onClick={handleSave}
                                        disabled={saving || isRecording || (!note.trim() && !selectedFile)}
                                        className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors shadow-sm disabled:opacity-50 flex items-center gap-2"
                                        title={isRecording ? "Pare a gravação primeiro" : ""}
                                    >
                                        {saving ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                                        Salvar
                                    </button>
                                </div>
                            </>
                        ) : activeTab === 'whatsapp' ? (
                            <ConversasWhatsApp negociacaoId={Number(negotiation.id)} />
                        ) : (
                            <div className="flex flex-col items-center justify-center py-8 text-gray-500 dark:text-slate-400 min-h-[140px]">
                                <div className="text-4xl mb-2 animate-pulse">😉</div>
                                <p className="font-semibold text-gray-700 dark:text-slate-200">Funcionalidade em breve</p>
                                <p className="text-xs">Estamos preparando a integração com Email.</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Task Bar */}
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-3 flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="text-gray-400 dark:text-slate-500"><CheckSquare size={20} /></div>
                        <span className="text-sm text-gray-600 dark:text-slate-300 font-medium">Este negócio possui {taskCount} atividade(s) em aberto</span>
                    </div>
                    <button
                        onClick={() => setIsVisitaModalOpen(true)}
                        className="px-4 py-1.5 bg-primary text-white rounded-lg text-xs font-bold hover:bg-primary/90 transition-colors flex items-center gap-2"
                    >
                        <CalendarClock size={14} /> Criar visita
                    </button>
                </div>

                {/* Filters */}
                <div className="flex gap-2 overflow-x-auto pb-1 hide-scrollbar">
                    {(['tudo', 'notas', 'anexos', 'tarefas', 'mensagens'] as TimelineFilter[]).map(f => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize whitespace-nowrap transition-colors
                            ${filter === f ? 'bg-primary/10 dark:bg-primary/20 text-primary border border-primary/20 dark:border-primary/30' : 'bg-white dark:bg-slate-900 text-gray-500 dark:text-slate-400 border border-transparent hover:bg-gray-50 dark:hover:bg-slate-800/80 dark:bg-slate-800'}
                        `}
                        >
                            {f}
                        </button>
                    ))}
                </div>

                {/* Termo Assinado Banner */}
                {negotiation.status === 'termo_assinado' && (
                    <div className="flex items-center gap-3 p-4 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800/50 rounded-xl shadow-sm animate-fade-in">
                        <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center flex-shrink-0">
                            <FileCheck2 size={20} className="text-teal-600 dark:text-teal-400" />
                        </div>
                        <div className="flex-1">
                            <p className="text-sm font-bold text-teal-800 dark:text-teal-300">Termo de Adesão Assinado</p>
                            <p className="text-xs text-teal-600 dark:text-teal-400 mt-0.5">
                                O cliente assinou o termo de adesão.
                                {negotiation.data_assinatura_termo && (
                                    <span className="font-semibold"> Assinado em {new Date(negotiation.data_assinatura_termo).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}.</span>
                                )}
                                {' '}A negociação está pronta para validação de dados.
                            </p>
                        </div>
                        <span className="px-2.5 py-1 bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 text-[10px] font-bold uppercase rounded-full tracking-wider flex-shrink-0">Assinado</span>
                    </div>
                )}

                {/* Timeline Stream */}
                <div className="relative pl-4 border-l-2 border-gray-100 dark:border-slate-700/50 space-y-6 pb-8">
                    {filteredEvents.map((event) => (
                        <div key={event.id} className="relative group">
                            <div className={`absolute -left-[21px] top-1 w-3 h-3 rounded-full border-2 border-white dark:border-slate-900
                            ${event.type === 'task' ? 'bg-blue-400' : event.type === 'log' ? 'bg-slate-400' : 'bg-primary'}
                        `}></div>

                            <div className="flex flex-col gap-1 p-3 rounded-xl bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-700/50 shadow-sm hover:shadow-md transition-shadow">
                                <div className="flex justify-between items-start">
                                    <div className="text-xs text-gray-500 dark:text-slate-400 flex items-center gap-2">
                                        <span className="font-bold text-gray-800 dark:text-slate-100">{event.user.name}</span>
                                        <span>•</span>
                                        <span>{event.date.toLocaleString()}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {/* Edit/Delete for own notes */}
                                        {(event.type === 'note' || event.type === 'attachment') && event.data.user_id === user?.id && (
                                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                {editingNoteId !== event.data.id && (
                                                    <>
                                                        <button
                                                            onClick={() => handleStartEdit(event.data.id, event.data.conteudo || '')}
                                                            className="p-1 text-gray-400 dark:text-slate-500 hover:text-primary hover:bg-primary/10 rounded-md transition-colors"
                                                            title="Editar nota"
                                                        >
                                                            <Pencil size={13} />
                                                        </button>
                                                        {deletingNoteId === event.data.id ? (
                                                            <div className="flex items-center gap-1 bg-red-50 dark:bg-red-900/20 px-2 py-0.5 rounded-md">
                                                                <span className="text-[10px] text-red-600 dark:text-red-400 font-medium">Excluir?</span>
                                                                <button
                                                                    onClick={() => handleDeleteNote(event.data.id)}
                                                                    className="p-0.5 text-red-600 dark:text-red-400 hover:text-red-800 transition-colors"
                                                                >
                                                                    <Check size={12} />
                                                                </button>
                                                                <button
                                                                    onClick={() => setDeletingNoteId(null)}
                                                                    className="p-0.5 text-gray-400 dark:text-slate-500 hover:text-gray-600 transition-colors"
                                                                >
                                                                    <X size={12} />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                onClick={() => setDeletingNoteId(event.data.id)}
                                                                className="p-1 text-gray-400 dark:text-slate-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                                                                title="Excluir nota"
                                                            >
                                                                <Trash2 size={13} />
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                        <div className="text-[10px] uppercase font-bold text-gray-400 dark:text-slate-500 tracking-wider p-0.5 bg-gray-50 dark:bg-slate-800 rounded">
                                            {event.type}
                                        </div>
                                    </div>
                                </div>

                                {/* Content: editable or display */}
                                {editingNoteId === event.data.id ? (
                                    <div className="mt-1 space-y-2">
                                        <textarea
                                            value={editingContent}
                                            onChange={(e) => setEditingContent(e.target.value)}
                                            className="w-full min-h-[60px] text-sm text-gray-700 dark:text-slate-200 placeholder-gray-400 dark:placeholder-slate-500 outline-none resize-none bg-gray-50 dark:bg-slate-800 rounded-lg p-2 border border-gray-200 dark:border-slate-700 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                        />
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={handleCancelEdit}
                                                className="px-3 py-1 text-xs font-medium text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                            >
                                                Cancelar
                                            </button>
                                            <button
                                                onClick={() => handleUpdateNote(event.data.id)}
                                                disabled={editSaving || !editingContent.trim()}
                                                className="px-3 py-1 text-xs font-medium bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-1"
                                            >
                                                {editSaving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                                                Salvar
                                            </button>
                                        </div>
                                    </div>
                                ) : event.content.startsWith('[APPROVAL_REQUEST]') ? (
                                    <div className="mt-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/50 p-4 rounded-xl text-yellow-800 dark:text-yellow-400 text-sm">
                                        <h4 className="font-bold flex items-center gap-2 mb-2"><Sparkles size={16} /> Solicitação de Aprovação de Desconto</h4>
                                        <p className="mb-4">{event.content.replace('[APPROVAL_REQUEST] ', '')}</p>
                                        {(profile?.role === 'gerente' || profile?.role === 'adm' || profile?.role === 'gerente_parcerias') && (
                                            <div className="flex gap-2">
                                                {negotiation.desconto_status === 'pendente' && event.id === latestApprovalRequestId ? (
                                                    <>
                                                        <button onClick={() => handleApproveDiscount(event.data.id)} disabled={saving} className="px-4 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-colors text-xs shadow-sm flex items-center gap-2 disabled:opacity-50"><Check size={14} /> Aprovar</button>
                                                        <button onClick={() => handleRejectDiscount(event.data.id)} disabled={saving} className="px-4 py-2 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 transition-colors text-xs shadow-sm flex items-center gap-2 disabled:opacity-50"><X size={14} /> Rejeitar</button>
                                                    </>
                                                ) : (
                                                    <p className="text-xs font-medium italic opacity-70">
                                                        {negotiation.desconto_status === 'aprovado' ? '✅ Desconto aprovado' : negotiation.desconto_status === 'rejeitado' ? '❌ Desconto rejeitado' : 'Solicitação anterior'}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ) : event.content.startsWith('[DISCOUNT_APPROVED]') ? (
                                    <div className="mt-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 p-4 rounded-xl text-green-800 dark:text-green-400 text-sm">
                                        <h4 className="font-bold flex items-center gap-2 mb-1"><Check size={16} /> Desconto Aprovado</h4>
                                        <p>{event.content.replace('[DISCOUNT_APPROVED] ', '')}</p>
                                    </div>
                                ) : event.content.startsWith('[DISCOUNT_REJECTED]') ? (
                                    <div className="mt-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 p-4 rounded-xl text-red-800 dark:text-red-400 text-sm">
                                        <h4 className="font-bold flex items-center gap-2 mb-1"><X size={16} /> Desconto Rejeitado</h4>
                                        <p>{event.content.replace('[DISCOUNT_REJECTED] ', '')}</p>
                                    </div>
                                ) : event.content.startsWith('[APPROVAL_TRANSFERRED]') ? (
                                    <div className="mt-2 bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800/50 p-4 rounded-xl text-sky-800 dark:text-sky-400 text-sm">
                                        <h4 className="font-bold flex items-center gap-2 mb-1"><ArrowRightLeft size={16} /> Aprovação Transferida</h4>
                                        <p>{event.content.replace('[APPROVAL_TRANSFERRED] ', '')}</p>
                                    </div>
                                ) : event.content.startsWith('[APPROVAL_SNOOZE') ? (
                                    <div className="mt-2 bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700/60 p-4 rounded-xl text-slate-700 dark:text-slate-300 text-sm">
                                        <h4 className="font-bold flex items-center gap-2 mb-1"><Clock size={16} /> Aprovação Adiada</h4>
                                        <p>{event.content.replace(/^\[APPROVAL_SNOOZE:[^\]]+\]\s*/, '')}</p>
                                    </div>
                                ) : event.content.startsWith('[TERMO_ANALISE_TRANSFERIDA]') ? (
                                    <div className="mt-2 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800/50 p-4 rounded-xl text-teal-800 dark:text-teal-400 text-sm">
                                        <h4 className="font-bold flex items-center gap-2 mb-1"><ArrowRightLeft size={16} /> Validação de Dados Transferida</h4>
                                        <p>{event.content.replace('[TERMO_ANALISE_TRANSFERIDA] ', '')}</p>
                                    </div>
                                ) : event.content.startsWith('[TERMO_ANALISE_ADIADA') ? (
                                    <div className="mt-2 bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700/60 p-4 rounded-xl text-slate-700 dark:text-slate-300 text-sm">
                                        <h4 className="font-bold flex items-center gap-2 mb-1"><Clock size={16} /> Validação de Dados Adiada</h4>
                                        <p>{event.content.replace(/^\[TERMO_ANALISE_ADIADA:[^\]]+\]\s*/, '')}</p>
                                    </div>
                                ) : event.content.startsWith('[TROCA_TITULARIDADE_ENVIADA]') ? (() => {
                                    const [linha, link] = event.content.split('\n');
                                    return (
                                        <div className="mt-2 bg-primary/5 dark:bg-primary/10 border border-primary/20 dark:border-primary/30 p-4 rounded-xl text-sm">
                                            <h4 className="font-bold flex items-center gap-2 mb-1 text-primary"><FileIcon size={16} /> Troca de Titularidade Enviada</h4>
                                            <p className="text-gray-700 dark:text-slate-300">{linha.replace('[TROCA_TITULARIDADE_ENVIADA] ', '')}</p>
                                            {link && (
                                                <a href={link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-primary hover:underline">
                                                    <ExternalLink size={12} /> Link de assinatura
                                                </a>
                                            )}
                                        </div>
                                    );
                                })() : event.content.startsWith('[TROCA_TITULARIDADE_ASSINADA]') ? (() => {
                                    const [linha, link] = event.content.split('\n');
                                    return (
                                        <div className="mt-2 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800/50 p-4 rounded-xl text-sm">
                                            <h4 className="font-bold flex items-center gap-2 mb-1 text-teal-700 dark:text-teal-400"><FileCheck2 size={16} /> Troca de Titularidade Assinada</h4>
                                            <p className="text-teal-700 dark:text-teal-300">{linha.replace('[TROCA_TITULARIDADE_ASSINADA] ', '')}</p>
                                            {link && (
                                                <a href={link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-teal-700 dark:text-teal-400 hover:underline">
                                                    <ExternalLink size={12} /> Ver PDF assinado
                                                </a>
                                            )}
                                        </div>
                                    );
                                })() : event.content.startsWith('[DELETE_REQUEST]') ? (() => {
                                    const isResolved = events.some(e => e.content.startsWith('[DELETE_REJECTED]') && e.date > event.date);
                                    return (
                                        <div className="mt-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 p-4 rounded-xl text-red-800 dark:text-red-400 text-sm">
                                            <h4 className="font-bold flex items-center gap-2 mb-2"><Trash2 size={16} /> Solicitação de Exclusão de Negociação</h4>
                                            <p className="mb-4">{event.content.replace('[DELETE_REQUEST] ', '')}</p>
                                            {!isResolved ? (
                                                (profile?.role === 'gerente' || profile?.role === 'adm' || profile?.role === 'gerente_parcerias') ? (
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => handleApproveDelete(event.data.id)}
                                                            disabled={saving}
                                                            className="px-4 py-2 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 transition-colors text-xs shadow-sm flex items-center gap-2 disabled:opacity-50"
                                                        >
                                                            <Check size={14} /> Aprovar Exclusão
                                                        </button>
                                                        <button
                                                            onClick={() => handleRejectDelete(event.data.id)}
                                                            disabled={saving}
                                                            className="px-4 py-2 bg-gray-600 text-white rounded-lg font-bold hover:bg-gray-700 transition-colors text-xs shadow-sm flex items-center gap-2 disabled:opacity-50"
                                                        >
                                                            <X size={14} /> Recusar e Manter
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <p className="text-xs font-medium text-red-600/70 dark:text-red-400/70 italic">
                                                        Aguardando decisão da gerência.
                                                    </p>
                                                )
                                            ) : (
                                                <p className="text-xs font-medium text-gray-500 italic">
                                                    Esta solicitação já foi processada.
                                                </p>
                                            )}
                                        </div>
                                    );
                                })() : event.content.startsWith('[DELETE_REJECTED]') ? (
                                    <div className="mt-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 p-4 rounded-xl text-gray-700 dark:text-slate-300 text-sm">
                                        <h4 className="font-bold flex items-center gap-2 mb-2 italic text-gray-500"><X size={16} /> Pedido de Exclusão Recusado</h4>
                                        <p>{event.content.replace('[DELETE_REJECTED] ', '')}</p>
                                    </div>
                                ) : event.content.startsWith('[PENDENCIA_DOCUMENTAL]') ? (() => {
                                    const isResolved = events.some(e => e.content.startsWith('[PENDENCIA_RESOLVIDA]') && e.content.includes(`ref:${event.data.id}`));
                                    const parts = event.content.replace('[PENDENCIA_DOCUMENTAL] ', '');
                                    return (
                                        <div className={`mt-2 ${isResolved ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800/40' : 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800/50'} border p-4 rounded-xl text-sm`}>
                                            <h4 className={`font-bold flex items-center gap-2 mb-2 ${isResolved ? 'text-green-700 dark:text-green-400' : 'text-orange-800 dark:text-orange-400'}`}>
                                                <AlertTriangle size={16} />
                                                {isResolved ? '✅ Pendência Documental Resolvida' : '📋 Pendência Documental'}
                                            </h4>
                                            <p className={`mb-3 whitespace-pre-line ${isResolved ? 'text-green-700 dark:text-green-300' : 'text-orange-700 dark:text-orange-300'}`}>{parts}</p>
                                            {!isResolved && (profile?.role === 'vendedor' || profile?.role === 'vendedor_parceiro') && (
                                                <button
                                                    onClick={async () => {
                                                        const resposta = prompt('Descreva o que foi feito para resolver a pendência:');
                                                        if (!resposta?.trim()) return;
                                                        setSaving(true);
                                                        try {
                                                            await supabase.from('negociacao_logs').insert({
                                                                negociacao_id: negotiation.id,
                                                                acao: `[PENDENCIA_RESOLVIDA] ref:${event.data.id}`,
                                                                detalhes: resposta,
                                                                user_id: user?.id
                                                            });
                                                            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'resolver_pendencia_documental', origem: 'acao' });
                                                            if (event.data.user_id) {
                                                                await supabase.from('notifications').insert({
                                                                    user_id: event.data.user_id,
                                                                    type: 'pendencia_documental',
                                                                    title: 'Pendência Documental Resolvida',
                                                                    content: `O vendedor respondeu à pendência da negociação "${negotiation.cliente_nome}": ${resposta}`,
                                                                    link: `/crm?negociacaoId=${negotiation.id}`,
                                                                    read: false
                                                                });
                                                            }
                                                            addToast('Resposta enviada ao analista!', 'success');
                                                            fetchData();
                                                        } catch (err: any) {
                                                            addToast('Erro: ' + err.message, 'error');
                                                        } finally {
                                                            setSaving(false);
                                                        }
                                                    }}
                                                    disabled={saving}
                                                    className="px-4 py-2 bg-orange-600 text-white rounded-lg font-bold hover:bg-orange-700 transition-colors text-xs shadow-sm flex items-center gap-2 disabled:opacity-50"
                                                >
                                                    <Check size={14} /> Resolver Pendência
                                                </button>
                                            )}
                                            {isResolved && (
                                                <p className="text-xs text-green-600 dark:text-green-400 font-medium italic mt-1">Vendedor já respondeu a esta pendência.</p>
                                            )}
                                        </div>
                                    );
                                })() : event.content.startsWith('[PENDENCIA_RESOLVIDA]') ? (
                                    <div className="mt-2 bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800/40 p-4 rounded-xl text-emerald-800 dark:text-emerald-400 text-sm">
                                        <h4 className="font-bold flex items-center gap-2 mb-1"><Check size={16} /> Pendência Resolvida pelo Vendedor</h4>
                                        {event.data.detalhes && <p className="mt-1">{event.data.detalhes}</p>}
                                    </div>
                                ) : (
                                    <div className="text-sm text-gray-800 dark:text-slate-100 mt-1 space-y-0.5">
                                        {event.content.split('\n').map((line, i) => {
                                            const match = line.match(/^([\p{L} ]+):\s(.+)$/u);
                                            return (
                                                <div key={i}>
                                                    {match ? <><span className="font-semibold">{match[1]}:</span> {match[2]}</> : line}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* SubContent (Task Status, etc) */}
                                {event.subContent && <div className="mt-2">{event.subContent}</div>}

                                {/* Analysis Button */}
                                {((event.type === 'log' && (event.content.includes('Validação de Dados Concluída') || event.content.includes('Análise Cadastral Concluída'))) ||
                                    (event.type === 'note' && event.content.includes('[REABERTURA DE ANÁLISE]'))) && (
                                        <div className="mt-3">
                                            <button
                                                onClick={() => setIsAnalysisModalOpen(true)}
                                                className="text-xs px-3 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary font-medium rounded-lg transition-colors flex items-center gap-1.5 w-fit"
                                            >
                                                <FileIcon size={14} />
                                                Ver Análise Completa
                                            </button>
                                        </div>
                                    )}

                                {/* Task Detailed Info */}
                                {event.type === 'task' && event.data && (
                                    <div className="mt-3 space-y-4 border-t border-gray-100 dark:border-slate-800 pt-3">
                                        {/* Description */}
                                        {event.data.descricao && (
                                            <div className="text-sm text-gray-600 dark:text-slate-300 bg-gray-50 dark:bg-slate-800/50 p-3 rounded-lg border border-gray-100 dark:border-slate-800">
                                                {event.data.descricao}
                                            </div>
                                        )}

                                        {/* Info Grid */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {event.data.responsavel_profile && (
                                                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-slate-400">
                                                    <div className="p-1 bg-primary/10 rounded-md">
                                                        <User size={14} className="text-primary" />
                                                    </div>
                                                    <div>
                                                        <span className="font-bold block text-[10px] uppercase text-gray-400 dark:text-slate-500">Responsável</span>
                                                        <span>{event.data.responsavel_profile.full_name}</span>
                                                    </div>
                                                </div>
                                            )}
                                            {event.data.solicitante_profile && (
                                                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-slate-400">
                                                    <div className="p-1 bg-blue-100 dark:bg-blue-900/30 rounded-md">
                                                        <User size={14} className="text-blue-500" />
                                                    </div>
                                                    <div>
                                                        <span className="font-bold block text-[10px] uppercase text-gray-400 dark:text-slate-500">Solicitante</span>
                                                        <span>{event.data.solicitante_profile.full_name}</span>
                                                    </div>
                                                </div>
                                            )}
                                            {event.data.vencimento && (
                                                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-slate-400">
                                                    <div className="p-1 bg-orange-100 dark:bg-orange-900/30 rounded-md">
                                                        <Calendar size={14} className="text-orange-500" />
                                                    </div>
                                                    <div>
                                                        <span className="font-bold block text-[10px] uppercase text-gray-400 dark:text-slate-500">Vencimento</span>
                                                        {/* `vencimento` é DATE ("2026-08-19"): sem a hora o JS lê como
                                                            meia-noite UTC e mostra o dia anterior em UTC-3. */}
                                                        <span>{new Date(event.data.vencimento + 'T12:00:00').toLocaleDateString('pt-BR')}</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Checklist */}
                                        {event.data.checklists && event.data.checklists.length > 0 && (
                                            <div className="space-y-1.5">
                                                <div className="flex items-center gap-2 text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-widest px-1">
                                                    <List size={12} /> Checklist
                                                </div>
                                                <div className="bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 rounded-lg overflow-hidden">
                                                    {event.data.checklists.map((item: any) => (
                                                        <div key={item.id} className="flex items-center gap-2 px-3 py-2 border-b border-gray-50 dark:border-slate-800 last:border-0 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors">
                                                            {item.completed ? (
                                                                <CheckSquare size={14} className="text-green-500" />
                                                            ) : (
                                                                <Square size={14} className="text-gray-300 dark:text-slate-700" />
                                                            )}
                                                            <span className={`text-xs ${item.completed ? 'text-gray-400 dark:text-slate-500 line-through' : 'text-gray-700 dark:text-slate-200'}`}>
                                                                {item.text}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Comments */}
                                        {event.data.comentarios && event.data.comentarios.length > 0 && (
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2 text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-widest px-1">
                                                    <MessageSquare size={12} /> Comentários ({event.data.comentarios.length})
                                                </div>
                                                <div className="space-y-2">
                                                    {event.data.comentarios.map((c: any) => (
                                                        <div key={c.id} className={`bg-gray-50 dark:bg-slate-800/50 p-2.5 rounded-lg border border-gray-100 dark:border-slate-800 border-l-4 ${corDoUsuario(c.user_id).barra} flex gap-3`}>
                                                            {c.user?.avatar_url ? (
                                                                <img src={c.user.avatar_url} alt={c.user.full_name} className="w-6 h-6 rounded-full flex-shrink-0 object-cover" />
                                                            ) : (
                                                                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary flex-shrink-0">
                                                                    {c.user?.full_name?.charAt(0) || 'U'}
                                                                </div>
                                                            )}
                                                            <div className="flex-1">
                                                                <div className="flex justify-between items-center mb-0.5">
                                                                    <span className={`text-[11px] font-bold ${corDoUsuario(c.user_id).nome}`}>{c.user?.full_name || 'Usuário'}</span>
                                                                    <span className="text-[10px] text-gray-400 dark:text-slate-500">{new Date(c.created_at).toLocaleString([], { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}</span>
                                                                </div>
                                                                <ComentarioTexto texto={c.conteudo} className="text-xs text-gray-600 dark:text-slate-300" />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Action Button — visita abre "Concluir Visita"; tarefa comum abre "Gerenciar Tarefa" (ambos inline, sem sair do Kanban) */}
                                        <button
                                            onClick={() => event.data.tipo
                                                ? setVisitaTarefa(event.data)
                                                : setGerenciarTarefa(event.data)}
                                            className="text-xs px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 dark:text-blue-400 font-semibold rounded-lg transition-all flex items-center gap-2 w-full justify-center border border-blue-100 dark:border-blue-800/50 shadow-sm mt-2"
                                        >
                                            <CheckSquare size={14} />
                                            {event.data.tipo ? 'Concluir Visita' : 'Gerenciar Tarefa Completa'}
                                        </button>

                                        {/* Task Attachments */}
                                        {event.data.anexos_url && event.data.anexos_url.length > 0 && (
                                            <div className="space-y-2 mt-4 pt-4 border-t border-gray-100 dark:border-slate-800">
                                                <div className="flex items-center gap-2 text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-widest px-1">
                                                    <Paperclip size={12} /> Anexos ({event.data.anexos_url.length})
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    {event.data.anexos_url.map((url: string, idx: number) => {
                                                        const isImage = url.match(/\.(jpeg|jpg|gif|png|webp|avif)$/i);
                                                        const fileName = url.split('/').pop()?.split('_').slice(1).join('_') || `Anexo ${idx + 1}`;

                                                        return (
                                                            <div key={idx} className="flex flex-col gap-1">
                                                                {isImage ? (
                                                                    <a href={url} target="_blank" rel="noreferrer" className="block group/img relative">
                                                                        <img src={url} alt={fileName} className="h-20 w-20 object-cover rounded-lg border border-gray-200 dark:border-slate-700 hover:opacity-80 transition-opacity" />
                                                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                                                                            <Download size={16} className="text-white" />
                                                                        </div>
                                                                    </a>
                                                                ) : (
                                                                    <a
                                                                        href={url}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg w-fit hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                                                                    >
                                                                        <FileIcon size={16} className="text-gray-500 dark:text-slate-400" />
                                                                        <span className="text-xs text-gray-700 dark:text-slate-200 underline max-w-[150px] truncate">{fileName}</span>
                                                                        <Download size={14} className="text-gray-400 dark:text-slate-500" />
                                                                    </a>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Transcription Display */}
                                {event.data.transcricao && (
                                    <div className="mt-3 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-100 dark:border-orange-800/50">
                                        <div className="flex items-center gap-2 text-orange-700 dark:text-orange-400 font-bold text-xs mb-1 uppercase tracking-wide">
                                            <Sparkles size={12} />
                                            Transcrição
                                        </div>
                                        <p className="text-sm text-gray-700 dark:text-slate-200 italic">
                                            "{event.data.transcricao}"
                                        </p>
                                    </div>
                                )}

                                {/* Attachments Display */}
                                {event.data.arquivo_url && (
                                    <div className="mt-3">
                                        {event.data.arquivo_tipo?.startsWith('image/') ? (
                                            <img src={event.data.arquivo_url} alt="Attachment" className="max-w-xs rounded-lg border border-gray-200 dark:border-slate-700" />
                                        ) : event.data.arquivo_tipo?.startsWith('audio/') ? (
                                            <audio controls src={event.data.arquivo_url} className="w-full max-w-xs mt-1 dark:invert dark:hue-rotate-180 brightness-90" />
                                        ) : (
                                            <a
                                                href={event.data.arquivo_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg w-fit hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800/80 transition-colors"
                                            >
                                                <FileIcon size={16} className="text-gray-500 dark:text-slate-400" />
                                                <span className="text-sm text-gray-700 dark:text-slate-200 underline">{event.data.arquivo_nome || 'Anexo'}</span>
                                                <Download size={14} className="text-gray-400 dark:text-slate-500" />
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}

                    {filteredEvents.length === 0 && (
                        <div className="text-center py-8 text-gray-400 dark:text-slate-500 text-sm">
                            Nenhum registro encontrado.
                        </div>
                    )}
                </div>

                {isVisitaModalOpen && (
                    <CriarVisitaModal
                        negociacao={negotiation}
                        onClose={() => setIsVisitaModalOpen(false)}
                        onCreated={() => fetchData()}
                    />
                )}

                {isAnalysisModalOpen && (
                    <GestaoDetailsModal
                        negociacaoId={negotiation.id}
                        onClose={() => setIsAnalysisModalOpen(false)}
                        onReopenEdit={() => setIsAnalysisModalOpen(false)}
                        hideReviewButton={true}
                    />
                )}

                {/* Voice Confirmation Modal */}
                {showVoiceModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-md p-6 animate-in fade-in zoom-in duration-200">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Anexar nota de voz</h3>
                                <button onClick={() => setShowVoiceModal(false)} className="text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 dark:text-slate-300">
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-3 flex items-center gap-3 border border-gray-200 dark:border-slate-700 mb-6">
                                <div className="w-10 h-10 bg-gray-200 dark:bg-slate-700 rounded-full flex items-center justify-center text-gray-500 dark:text-slate-400">
                                    <Paperclip size={20} />
                                </div>
                                <div className="flex-1 overflow-hidden">
                                    <p className="text-sm font-medium text-gray-800 dark:text-slate-100 truncate">{tempAudioFile?.name}</p>
                                    <p className="text-xs text-gray-500 dark:text-slate-400">{(tempAudioFile?.size || 0) / 1024 > 1024 ? `${((tempAudioFile?.size || 0) / 1024 / 1024).toFixed(2)} MB` : `${((tempAudioFile?.size || 0) / 1024).toFixed(2)} KB`}</p>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => setShowVoiceModal(false)}
                                    className="px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-slate-200 font-medium hover:bg-gray-50 dark:hover:bg-slate-800/80 dark:bg-slate-800 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={confirmVoiceNote}
                                    className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
                                >
                                    Salvar
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            </div>

            <ConfirmModal
                isOpen={showDeleteApproveConfirm}
                onClose={() => setShowDeleteApproveConfirm(false)}
                onConfirm={executeApproveDelete}
                title="Aprovar Exclusão"
                message={`Tem certeza que deseja APROVAR a exclusão da negociação "${negotiation.cliente_nome}"? Todas as UCs, tarefas, notas e logs serão removidos permanentemente. Esta ação não pode ser desfeita.`}
                confirmLabel="Sim, aprovar exclusão"
                cancelLabel="Cancelar"
                variant="danger"
                loading={saving}
            />

            {/* Painel "Concluir Visita" inline (reuso das Rotas) p/ tarefas do tipo visita */}
            {visitaTarefa && (
                <VisitaModal
                    parada={{
                        id: String(visitaTarefa.id),
                        titulo: negotiation.titulo,
                        checkin_em: null,
                        cidade: negotiation.cidade ?? null,
                        bairro: negotiation.bairro ?? null,
                        segmento: negotiation.segmento ?? null,
                        telefone: negotiation.whatsapp ?? null,
                        kwh: negotiation.consumo_medio_kwh ?? null,
                        modalidade: normalizarModalidade(visitaTarefa.modalidade),
                        negociacaoId: negotiation.id,
                    } as VisitaParadaInfo}
                    etapa="encerrar"
                    modo="comercial"
                    onClose={() => setVisitaTarefa(null)}
                    onIniciar={async () => { /* tarefa de visita não tem check-in */ }}
                    onEncerrar={async (_p, dados: VisitaConclusao) => {
                        try {
                            await concluirVisitaTarefa({ ...visitaTarefa, negociacao: { titulo: negotiation.titulo } }, dados, user?.id);
                            addToast(dados.proximaVisitaData ? 'Visita concluída e próxima agendada! ✅' : 'Visita concluída! ✅', 'success');
                            setVisitaTarefa(null);
                            await fetchData();
                            onUpdate?.();
                            window.dispatchEvent(new CustomEvent('refreshKanban'));
                        } catch (err) {
                            console.error('Erro ao concluir visita:', err);
                            addToast('Erro ao concluir a visita.', 'error');
                        }
                    }}
                    onAbrirLead={() => setVisitaTarefa(null)}
                    onChangeModalidade={async (m) => {
                        await supabase.from('tarefas').update({ modalidade: m }).eq('id', visitaTarefa.id);
                        logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'mudar_modalidade_visita', origem: 'acao' });
                        setVisitaTarefa((prev: any) => prev ? { ...prev, modalidade: m } : prev);
                    }}
                />
            )}

            {/* "Gerenciar Tarefa" inline (tarefa comum) — resolve sem sair do Kanban */}
            {gerenciarTarefa && (
                <GerenciarTarefaModal
                    tarefa={gerenciarTarefa}
                    negotiation={negotiation}
                    onClose={() => setGerenciarTarefa(null)}
                    onAbrirCompleta={() => window.open(`/#/tarefas?id=${gerenciarTarefa.id}`, '_blank')}
                    onConfirm={async (r: GerenciarTarefaResultado) => {
                        const tarefa = { ...gerenciarTarefa, negociacao_id: gerenciarTarefa.negociacao_id ?? negotiation.id };
                        try {
                            if (r.acao === 'concluir') {
                                await concluirTarefa(tarefa, {
                                    descricao: r.observacao,
                                    proximaVisitaData: r.proximaVisitaData || null,
                                    proximaVisitaModalidade: r.proximaVisitaModalidade,
                                }, user?.id);
                                addToast(r.proximaVisitaData ? 'Tarefa concluída e retorno agendado! ✅' : 'Tarefa concluída! ✅', 'success');
                            } else if (r.acao === 'reagendar') {
                                await reagendarTarefa(tarefa, r.novaData!, user?.id, gerenciarTarefa.vencimento ? String(gerenciarTarefa.vencimento).slice(0, 10) : undefined);
                                addToast('Tarefa reagendada! 📅', 'success');
                            } else {
                                await cancelarTarefa(tarefa, r.observacao, user?.id);
                                addToast('Tarefa cancelada.', 'info');
                            }
                            setGerenciarTarefa(null);
                            await fetchData();
                            onUpdate?.();
                            window.dispatchEvent(new CustomEvent('refreshKanban'));
                        } catch (err) {
                            console.error('Erro ao gerenciar tarefa:', err);
                            addToast('Erro ao gerenciar a tarefa.', 'error');
                        }
                    }}
                />
            )}

        </>
    );
};
export default DetailFeed;
